import { Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { GetCustomerPortalDataResponsePayload } from '@vy/api-definition';
import type { CustomerPortalInvoice } from '@vy/types';
import type { ItemOf } from '@vy/utils';
import { isNotNullOrUndefined } from '@vy/utils';
import { Price } from '../../../../../../components/design/Price';
import { compareOptionalDatesDescending, formatDate } from '../../../../../../utils/date';
import { InvoiceBillingStatusLabel } from '../../../../../invoices/InvoicesPage/invoices-page-components/InvoiceBillingStatusLabel';
import { CustomerPortalInvoicesTableDownloadButton } from '../customer-portal-invoices-table-components/CustomerPortalInvoicesTableDownloadButton';
import { CustomerPortalInvoicesTableViewButton } from '../customer-portal-invoices-table-components/CustomerPortalInvoicesTableViewButton';

export const CUSTOMER_PORTAL_INVOICES_ROW_ACTIONS_CLASSNAME = 'customer-portal-invoices-row-actions';

export const CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS = {
  BILLING_STATUS: 'billingStatus',
  ISSUED_AT: 'issuedAt',
  DUE_DATE: 'dueDate',
  TOTAL_AMOUNT: 'revenueBreakdown.total',
  ID: 'id',
  ACTIONS: 'actions',
  DIVIDER: 'divider',
} as const;

export type CustomerPortalInvoicesColumnsKey = keyof typeof CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS;

type InvoiceRow = {
  id: string;
  invoice: ItemOf<GetCustomerPortalDataResponsePayload['invoices']>;
  token: string;
  onViewInvoice: (invoice: CustomerPortalInvoice) => void;
};

export const CUSTOMER_PORTAL_INVOICES_TABLE_COLUMNS: GridColDef<InvoiceRow>[] = [
  {
    field: CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS.ID,
    headerName: 'Invoice',
    flex: 1,
    valueGetter: (_value, row) => row.invoice.invoiceNumber,
    renderCell: ({ value }) => <span className="font-sans text-black">{value}</span>,
  },
  {
    field: CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS.ISSUED_AT,
    headerName: 'Issue Date',
    width: 200,
    valueFormatter: (value) => (isNotNullOrUndefined(value) ? formatDate(value) : ''),
    sortComparator: compareOptionalDatesDescending,
    valueGetter: (_value, row) => row.invoice.issuedAt,
  },
  {
    field: CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS.DUE_DATE,
    headerName: 'Due Date',
    width: 200,
    valueFormatter: (value) => (isNotNullOrUndefined(value) ? formatDate(value) : ''),
    sortComparator: compareOptionalDatesDescending,
    valueGetter: (_value, row) => row.invoice.dueDate,
  },
  {
    field: CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS.TOTAL_AMOUNT,
    headerName: 'Amount',
    width: 200,
    valueGetter: (_value, row) => row.invoice.revenueBreakdown.total,
    renderCell: (params) => {
      const amount = params.row.invoice.revenueBreakdown.total;
      const currency = params.row.invoice.currency;

      return (
        <span className="text-label font-sans">
          <Price className="font-sans text-black" sx={{ fontSize: 14 }} currency={currency}>
            {amount}
          </Price>
        </span>
      );
    },
  },
  {
    field: CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS.BILLING_STATUS,
    headerName: 'Status',
    valueGetter: (_value, row) => row.invoice.billingStatus,
    headerClassName: 'pl-7',
    cellClassName: 'pl-6',
    width: 200,
    hideSortIcons: true,
    resizable: false,
    renderCell: ({ row }) => (
      <InvoiceBillingStatusLabel
        billingStatus={row.invoice.billingStatus}
        isTrial={row.invoice.isTrial}
      />
    ),
  },
  {
    field: CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS.ACTIONS,
    headerName: '',
    // width: 100,
    flex: 1,
    sortable: false,
    resizable: false,
    align: 'center',
    cellClassName: CUSTOMER_PORTAL_INVOICES_ROW_ACTIONS_CLASSNAME,
    renderCell: ({ row }) => (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
        <CustomerPortalInvoicesTableViewButton invoice={row.invoice} onViewInvoice={row.onViewInvoice} />
        <CustomerPortalInvoicesTableDownloadButton invoice={row.invoice} token={row.token} />
      </Stack>
    ),
  },
];
